import { useSetRecoilState } from "recoil"

import useQuery from "../useQuery"
import layoutStates from "../../recoil/atoms/layout"
import { viewSelector } from "../../recoil/atoms/view"
import { IS_RECORDING_VIEW, Views } from "../../constants"
import { localUserNameAtom } from "../../recoil/atoms/users"
import { setPrivateRoomSelector } from "../../recoil/atoms/privateRoom"
import { roomSelector, brandingSelector } from "../../recoil/atoms/room"
import { RoomBranding } from "src/recoil/atoms/room/types"
import { SDKContextType } from "src/context/types"
import { getConfig } from "@testing-library/react"
import { getConfigs } from "../useConfig"

type BinaryFlag = "0" | "1" | 0 | 1
function isBinaryFlag(item: unknown): item is BinaryFlag {
  if (item === "0" || item === "1" || item === 0 || item === 1) {
    return true
  }
  return false
}

function createBoolean(value: BinaryFlag) {
  return value === "1" || value === 1 ? true : false
}

function binaryFlagToBoolean(branding: RoomBranding): RoomBranding {
  const record: Record<string, any> = {}
  for (const [key, value] of Object.entries(branding)) {
    if (isBinaryFlag(value)) {
      record[key] = createBoolean(value)
    } else {
      record[key] = value
    }
  }
  return record as unknown as RoomBranding
}

export const useValidateRoom = (meetingClient: SDKContextType | null) => {
  const query = useQuery()
  const setRoom = useSetRecoilState(roomSelector)
  const setBranding = useSetRecoilState(brandingSelector)
  const setView = useSetRecoilState(viewSelector)
  const setPrivateRoom = useSetRecoilState(setPrivateRoomSelector)
  const setCurrentUserName = useSetRecoilState(localUserNameAtom)

  const setRecordingView = useSetRecoilState(layoutStates.recordingView)

  const validatePrivateRoom = () => {
    const response = meetingClient?.checkAndSetPrivateRoom()
    if (response?.isPrivateRoom) {
      setPrivateRoom({
        isPrivateRoom: response.isPrivateRoom,
        privateRoomData: response.privateRoomData
      })
    }
  }

  const validateRoom = async ({ joinRoomDirectly = false } = {}) => {
    try {
      if (meetingClient) {
        const resp = await meetingClient.validateAndGetRoomBranding()
        if (resp.status === "OK") {
          setRoom({ name: "ADD", attributes: resp.room })

          // initialize Agora, if voice_and_video_enabled is enabled
          const { voiceAndVideoEnabled } = await getConfigs()
          if (voiceAndVideoEnabled !== false && meetingClient.rtc) {
            await meetingClient.rtc.initializeRTC()
          }

          if (resp && resp.room && resp.room.branding) {
            setBranding(binaryFlagToBoolean(resp.room.branding))
            // Util.setPageAttributes(resp.room.branding)
          }
          if (IS_RECORDING_VIEW) {
            setRecordingView(true)
            setView(Views.RECORDING_VIEW)
          } else {
            if (
              resp &&
              resp.room &&
              resp.room.branding &&
              resp.room.branding.modNotPresentEventTracker
            ) {
              meetingClient.activateReplayEventTracker(
                resp.room.branding.modNotPresentEventTracker
              )
            }

            if (joinRoomDirectly) {
              setView(Views.LOADING)
              meetingClient?.connectGateway()
            } else if (query.autoJoin && query.userName) {
              // auto Join the meeting
              setCurrentUserName(query.userName as string)
              meetingClient?.connectGateway()

              // set avatar
              if (query.avatar) {
                meetingClient?.setUserAvatar(query.avatar + "")
              }
            } else {
              setView(Views.HOME)
            }
          }
        } else {
          // Handle two error - INVALID_ROOM_HASH && ROOM_EXPIRED
          setView(Views.NOT_FOUND)
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  return {
    validateRoom,
    validatePrivateRoom
  }
}
